/**
 * Service layer — Design Presentations domain (Round 10).
 *
 * The four client-facing design presentations (Conceptual Framework, Integrated
 * Design Proposal, Interior Concept Package, Interior Design Refinement) are all
 * built on a single Project Concepts row (tblaAwLsCi6XGsdFY). Each presentation
 * TYPE reads a different group of fields on that row + a few child tables.
 *
 * This file is the ONLY place that knows Airtable table IDs / field names for
 * presentations. It normalizes a record + its children into a clean, type-keyed
 * object whose `sections` array maps 1:1 to the present/ section registry:
 *
 *   { id, recordId, type, title, project:{name,city,state},
 *     publishState, raw, sections:[ { id, type, data } ] }
 *
 * SECURITY: every call routes through /api/airtable (services/_api.js). The old
 * Softr blocks held a hardcoded Airtable PAT in the browser — that is retired.
 *
 * Foundation phase ships the Interior Concept Package (ICP) normalizer in full.
 * CF / IDP / IDR builders are stubbed (data-only follow-up — see buildSections).
 */

import { apiGet, apiGetOne, apiPatch, apiPost, apiDelete, apiPatchBatch } from './_api'
import { PRESENTATION_TYPE_KEY } from '../outline/constants.js'

/* ── Table IDs ───────────────────────────────────────────────────────────── */
const TBL_CONCEPT  = 'tblaAwLsCi6XGsdFY'   // Project Concepts (the presentation root)
const TBL_SKETCH   = 'tblQYaWR9pUmiAwZe'   // Concept Sketches
const TBL_ROOMS    = 'tbl7sWrKq7bmvctSx'   // Rooms and Spaces
const TBL_ROOM_TPL = 'tblslHjXgMJb59po0'   // Room Templates (pool/spa size templates)
const TBL_COLLAGE  = 'tblcsAtLfjVAPZLB4'   // Collage Elements
const TBL_MATERIAL = 'tblzDPIm20pQmjemv'   // Materials
const TBL_DELIV    = 'tblFuiDpahqCL4eIK'   // Project Deliverables (milestones)

/* ── Attachment helpers ──────────────────────────────────────────────────── */
function attUrl(att) {
  if (!att) return ''
  return att.url || (att.thumbnails && att.thumbnails.large && att.thumbnails.large.url) || ''
}
function attUrls(arr) { return (arr || []).map(attUrl).filter(Boolean) }
function firstAtt(arr) { return attUrl((arr || [])[0]) }
function safeJson(str, fallback) { try { return JSON.parse(str || '') } catch (e) { return fallback } }
/* Slot = { url, attId } so the edit layer can rebuild a multi-attachment array
   without clobbering untouched slots (keep others as {id}, change one as {url}). */
function attSlot(att) { return { url: attUrl(att), attId: (att && att.id) || null } }
/* Slot whose image lives in its OWN single-attachment field (CF/IDP numbered
   image fields). Carries `field` so the edit layer writes that exact field. */
function fieldSlot(fields, fieldName) {
  const att = (fields[fieldName] || [])[0]
  return { url: attUrl(att), attId: (att && att.id) || null, field: fieldName }
}
function padSlots(arr, n) {
  const out = (arr || []).slice(0, n).map(attSlot)
  while (out.length < n) out.push({ url: '', attId: null })
  return out
}

/* Fetch a set of records by ID, preserving the given order. */
async function getByIds(tbl, ids, sortField) {
  const list = (ids || []).filter(Boolean)
  if (!list.length) return []
  const formula = 'OR(' + list.map(id => 'RECORD_ID()="' + id + '"').join(',') + ')'
  let qs = 'filterByFormula=' + encodeURIComponent(formula) + '&pageSize=100'
  if (sortField) qs += '&sort%5B0%5D%5Bfield%5D=' + encodeURIComponent(sortField) + '&sort%5B0%5D%5Bdirection%5D=asc'
  const recs = await apiGet(tbl, qs)
  if (sortField) return recs   // server-sorted
  const byId = {}; recs.forEach(r => { byId[r.id] = r })
  return list.map(id => byId[id]).filter(Boolean)
}

/* ── Material chip normalization (unifies CF/IDP/ICP divergence) ──────────── */
const ASPECTS = ['1/1', '4/3', '3/4', '3/2', '2/3', '16/9', '9/16']
function normDisplayMode(m) {
  if (m === 'float') return 'transparent'        // IDP called transparent "float"
  return (m === 'fit' || m === 'transparent') ? m : 'fill'
}
function normLabelPos(p) {
  const ok = ['top', 'bottom', 'left', 'right', 'off']
  return ok.indexOf(p) !== -1 ? p : 'bottom'
}
function normAspect(a) { return ASPECTS.indexOf(a) !== -1 ? a : '1/1' }

/* Native materials tray is 1100×680 (scaled by CSS transform). Layout-blob keys
   match the original exactly: x,y,w in PX; z; labelPos; labelHidden; aspect;
   displayOpt. We pass them through so existing saved layouts render identically. */
export const TRAY_W = 1100
export const TRAY_H = 680
function buildMaterialChips(materialRecords, layoutBlob) {
  const layout = layoutBlob || {}
  return (materialRecords || []).map((rec, i) => {
    const f = rec.fields || {}
    const L = layout[rec.id] || {}
    return {
      id: rec.id,
      src: firstAtt(f['Material Image']),
      label: f['Material Name'] || '',
      x: typeof L.x === 'number' ? L.x : (40 + (i % 5) * 200),   // px fallback scatter
      y: typeof L.y === 'number' ? L.y : (40 + Math.floor(i / 5) * 220),
      w: typeof L.w === 'number' ? L.w : 165,                     // px (original clamp 140–200)
      z: typeof L.z === 'number' ? L.z : (i + 1),
      labelPos: L.labelHidden ? 'off' : normLabelPos(L.labelPos),
      displayMode: normDisplayMode(L.displayOpt || L.displayMode),
      aspect: normAspect(L.aspect),
    }
  })
}

/* ── Section builders, keyed by presentation type ─────────────────────────── */

/* Three fixed themes with hard-coded layouts (matches the original exactly):
   Materiality→tww(3), Interior Form→4alt(4), Experience→5alt(5). */
function icpThemes(f) {
  const defs = [
    { key: 'materiality', heading: 'Materiality and Atmosphere', field: 'Materiality and Atmosphere', imgs: 'Materiality and Atmosphere Images', layout: 'tww',  maxImgs: 3 },
    { key: 'form',        heading: 'Interior Form and Features',  field: 'Interior Form and Features',  imgs: 'Interior Form and Features Images',  layout: '4alt', maxImgs: 4 },
    { key: 'experience',  heading: 'Experience and Use',          field: 'Experience and Use',          imgs: 'Experience and Use Images',          layout: '5alt', maxImgs: 5 },
  ]
  return defs
    .map((d, i) => ({
      id: 'theme:' + d.key,
      type: 'narrative',
      data: {
        heading: d.heading,
        body: f[d.field] || '',
        bodyField: d.field,
        layout: d.layout,
        maxImgs: d.maxImgs,
        imgField: d.imgs,
        images: padSlots(f[d.imgs], d.maxImgs),
        bg: i % 2 === 0 ? 'bg' : 'card',     // alternating theme backgrounds
      },
    }))
  // Always emit all three themes; ThematicSection hides empty ones in present mode
  // (and shows them in edit mode). Keeping the normalizer mode-agnostic supports
  // the live Edit/Present toggle without re-fetching.
}

function icpKeywords(f) {
  const items = []
  for (let i = 1; i <= 3; i++) {
    items.push({
      word: f['Interior Keyword ' + i] || '',
      wordField: 'Interior Keyword ' + i,
      description: f['Interior Keyword ' + i + ' Description'] || '',
      descField: 'Interior Keyword ' + i + ' Description',
      image: attSlot((f['Interior Keyword ' + i + ' Image'] || [])[0]),
      imgField: 'Interior Keyword ' + i + ' Image',
    })
  }
  return items
}

async function buildIcpSections(record) {
  const f = record.fields || {}
  const sections = []

  // Splash / hero — doc-type label + project name as the title + location line
  const city = f['Project City'] || ''
  const state = (f['Project State (from Project)'] || [])[0] || f['Project State (from Project)'] || ''
  sections.push({
    id: 'splash', type: 'splash',
    data: {
      title: 'Interior Concept Package',
      subtitle: f['Project Name'] || '',
      location: [city, state].filter(Boolean).join(', '),
    },
  })

  // Concept brief — 38% text / 5-image mosaic
  sections.push({
    id: 'brief', type: 'brief',
    data: {
      heading: 'Concept Brief',
      body: f['Interior Concept Narrative'] || '',
      bodyField: 'Interior Concept Narrative',
      layout: 'brief',
      imgField: 'Interior Concept Narrative Images',
      images: padSlots(f['Interior Concept Narrative Images'], 5),
    },
  })

  // Keywords (always the 3 cards; KeywordsGrid hides empties in present mode)
  sections.push({ id: 'keywords', type: 'keywords', data: { items: icpKeywords(f) } })

  // Materiality themes
  icpThemes(f).forEach(s => sections.push(s))

  // Rooms + collages
  const roomIds = f['Rooms and Spaces'] || []
  if (roomIds.length) {
    const rooms = await getByIds(TBL_ROOMS, roomIds)
    // Each room links its collage elements via the `ICP Collage Elements` field
    // (the collages do not link back to the concept). Gather those ids + fetch.
    const collageIds = rooms.flatMap(r => r.fields['ICP Collage Elements'] || [])
    const collages = collageIds.length ? await getByIds(TBL_COLLAGE, collageIds) : []
    const collageByRoom = {}
    collages.forEach(c => {
      const roomLink = (c.fields['Room'] || [])[0]
      if (!roomLink) return
      ;(collageByRoom[roomLink] = collageByRoom[roomLink] || []).push({
        id: c.id,
        src: firstAtt(c.fields['Image']),
        label: c.fields['Label'] || '',
        pos: safeJson(c.fields['Position'], { x: 20, y: 20, scale: 1, rotation: 0, z: 1 }),
      })
    })
    const roomData = rooms.map(r => ({
      id: r.id,
      name: r.fields['Room'] || '',
      notes: r.fields['Room Notes'] || '',
      imgField: 'ICP Images',
      images: padSlots(r.fields['ICP Images'], 3),
      elements: (collageByRoom[r.id] || []).filter(e => e.src),
    }))
    if (roomData.length) sections.push({ id: 'collages', type: 'collages', data: { rooms: roomData } })
  }

  // Materials board — always include the section (MaterialsBoard hides itself in
  // present/client mode when empty; edit mode shows an empty tray to add to).
  const matIds = f['ICP Concept Materials'] || []
  const materials = matIds.length ? await getByIds(TBL_MATERIAL, matIds, 'Sort Order') : []
  const matLayout = safeJson(f['ICP Material Board Layout'], {})
  sections.push({ id: 'materials', type: 'materials', data: { chips: buildMaterialChips(materials, matLayout) } })

  // Sketches — always include (SketchWall hides itself when empty + not editing).
  const sketches = await getSketches(record.id, 'Interior Concept Package')
  sections.push({ id: 'sketches', type: 'sketches', data: { sketches, includeValue: 'Interior Concept Package', projectId: (f['Project'] || [])[0] || null } })

  return sections
}

async function getSketches(recordId, includeValue) {
  const formula =
    'AND(FIND("' + recordId + '",ARRAYJOIN({Record UID (from Project Concepts)},",")),' +
    'FIND("' + includeValue + '",{Include in Package}&""))'
  const qs = 'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc'
  const recs = await apiGet(TBL_SKETCH, qs)
  return recs.map(r => {
    const sf = r.fields || {}
    return {
      id: r.id,
      src: firstAtt(sf['Sketch File']),
      title: sf['Sketch Title'] || '',
      sketchType: sf['Sketch Type'] || '',
      sort: sf['Sort Order'] || 0,
    }
  }).filter(s => s.src)
}

/* type → builder + the publish field name on the concept record */
/* ── Conceptual Framework ────────────────────────────────────────────────── */

// Ordered image slots each CF theme carries, independent of its chosen layout — the layout
// (max 6-up) decides how many render; the rest persist hidden. Matches the layout catalog's cap.
const CF_MAX_SLOTS = 6

// `cs` = the collage `Concept Section` tag (ampersand form, matching the original
// CF_SECTION_LABELS) used to attach free-form collage elements to each theme.
const CF_THEMES = [
  { key: 'setting',   field: 'Setting and Site',          layout: 'tww',   n: 3, def: 'Setting and Site',          cs: 'Setting & Site' },
  { key: 'form',      field: 'Form and Massing',          layout: '4alt',  n: 4, def: 'Form and Massing',          cs: 'Form & Massing' },
  { key: 'materials', field: 'Materials and Surfaces',    layout: '5alt',  n: 5, def: 'Materials and Surfaces',    cs: 'Materials & Surfaces' },
  { key: 'approach',  field: 'Approach and Entry',        layout: '3tall', n: 3, def: 'Approach and Entry',        cs: 'Approach & Entry' },
  { key: 'openings',  field: 'Openings and Transparency', layout: '3tall', n: 3, def: 'Openings and Transparency', cs: 'Openings & Transparency' },
  { key: 'landscape', field: 'Landscape Interface',       layout: '3tall', n: 3, def: 'Landscape Interface',       cs: 'Landscape Interface' },
  { key: 'outdoor',   field: 'Outdoor Rooms',             layout: '3tall', n: 3, def: 'Outdoor Rooms',             cs: 'Outdoor Rooms' },
  { key: 'night',     field: 'Night Presence',            layout: '3tall', n: 3, def: 'Night Presence',            cs: 'Night Presence' },
]

/**
 * Fetch this concept's CF section collages, grouped by `Concept Section` label.
 * Server-side filter narrows to the 8 CF section tags; client-side we keep only
 * rows whose `Linked Concept` (multipleRecordLinks → reliable recXXX ids) matches
 * this concept, falling back to the `Record UID (from Linked Concept)` lookup.
 */
async function getCfCollages(conceptId) {
  const labels = CF_THEMES.map(t => t.cs)
  const formula = 'OR(' + labels.map(l => '{Concept Section}="' + l + '"').join(',') + ')'
  let recs = []
  try { recs = await apiGet(TBL_COLLAGE, 'filterByFormula=' + encodeURIComponent(formula)) } catch (e) { return {} }
  const bySection = {}
  recs.forEach(c => {
    const cf = c.fields || {}
    const links = cf['Linked Concept'] || []
    const uid = cf['Record UID (from Linked Concept)']
    const uidStr = Array.isArray(uid) ? uid.join(',') : String(uid || '')
    if (!links.includes(conceptId) && uidStr.indexOf(conceptId) === -1) return
    const src = firstAtt(cf['Image'])
    if (!src) return
    const sec = cf['Concept Section']
    ;(bySection[sec] = bySection[sec] || []).push({
      id: c.id,
      src,
      label: cf['Label'] || '',
      pos: safeJson(cf['Position'], { x: 20, y: 20, scale: 1, rotation: 0, z: 1 }),
    })
  })
  return bySection
}

async function buildCfSections(record) {
  const f = record.fields || {}
  const sections = []
  const city = f['Project City'] || ''
  const state = (f['Project State (from Project)'] || [])[0] || f['Project State (from Project)'] || ''

  sections.push({ id: 'splash', type: 'splash', data: { title: 'Conceptual Framework', subtitle: f['Project Name'] || '', location: [city, state].filter(Boolean).join(', ') } })

  // Sketches — moved directly under the splash (was after Parti/Keywords, both removed per
  // request: the deck now opens on working sketches instead of a written concept intro).
  const sketches = await getSketches(record.id, 'Conceptual Framework')
  sections.push({ id: 'sketches', type: 'sketches', data: { sketches, includeValue: 'Conceptual Framework', projectId: (f['Project'] || [])[0] || null } })

  // Free-form collage elements per theme (grouped by Concept Section tag).
  const collagesBySection = await getCfCollages(record.id)

  // 8 theme sections (editable titles + per-slot image fields). Each theme carries CF_MAX_SLOTS
  // ordered image slots regardless of its default layout — the chosen layout (from the layouts map
  // in context, falling back to `layout` here) decides how many render; slots past the layout's
  // count persist in storage (imageMap) but stay hidden. Slots beyond the theme's original Airtable
  // fields (e.g. Image 4-6) simply have no Airtable fallback and live purely in the imageMap.
  CF_THEMES.forEach((t, i) => {
    sections.push({
      id: 'theme:' + t.key, type: 'narrative',
      data: {
        heading: f[t.field + ' Title'] || t.def, headingField: t.field + ' Title', headingDefault: t.def,
        body: f[t.field] || '', bodyField: t.field,
        layout: t.layout,                 // default; overridden per-section by the layouts map
        selectableLayout: true,           // enables the per-section layout picker (CF themes)
        images: Array.from({ length: CF_MAX_SLOTS }, (_, k) => fieldSlot(f, t.field + ' Image ' + (k + 1))),
        bg: i % 2 === 0 ? 'card' : 'bg',
        collageSection: t.cs,
        collages: collagesBySection[t.cs] || [],
      },
    })
  })

  // Budget Explorer (control budget) — bottom of the deck
  sections.push({ id: 'budget', type: 'budget', data: await getBudgetData(record) })

  return sections
}

/* ── Budget Explorer data (shared by CF + IDP) ───────────────────────────── */

/**
 * Supply the Budget Explorer with the concept's program spaces (from Rooms and
 * Spaces) + any Control Budget Models lookup overrides on the concept record.
 * The component owns the baseline cost model + all math; this only fetches data.
 * Returns { spaces:[{id,name,cls,netSF,cf,scope}], overrides:{...} }.
 */
async function getBudgetData(record) {
  const f = record.fields || {}
  const lk = n => { const v = f[n]; return Array.isArray(v) ? v[0] : v }
  const num = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n }
  const pair = base => ({
    low:  num(lk(base + ' Cost Per SF Low (from Control Budget Models)')),
    high: num(lk(base + ' Cost Per SF High (from Control Budget Models)')),
  })
  const overrides = {
    gaf: num(lk('Gross Area Factor (from Control Budget Models)')),
    // Per-category $/SF straight from the Control Budget Model (every category,
    // not just occ/gar) so the explorer tracks the model exactly.
    model: {
      occ:   pair('Occupiable'),
      gar:   pair('Garage'),
      sto:   pair('Storage Utility'),
      deck:  pair('Elevated Deck'),
      patio: pair('Covered Patio'),
      site:  pair('Site Improvement'),
    },
    factors: {
      'Major Alteration':    num(lk('Major Alteration Factor (from Control Budget Models)')),
      'Heavy Alteration':    num(lk('Heavy Alteration Factor (from Control Budget Models)')),
      'Moderate Alteration': num(lk('Moderate Alteration Factor (from Control Budget Models)')),
      'Light Alteration':    num(lk('Light Alteration Factor (from Control Budget Model)')),       // singular 'Model' in Airtable
      // 'Cosmetic Refresh Factor (from Control Budget Model)' doesn't exist in Airtable yet — see
      // docs/PROGRAM_MODEL.md "Scope Type — cosmetic-refresh gap". lk() returns undefined until
      // that field (and its Rooms/Project Concepts lookup chain) is added; BudgetExplorer's own
      // BASELINE_F.Cosmetic Refresh (0.10) covers every concept until then.
      'Cosmetic Refresh':    num(lk('Cosmetic Refresh Factor (from Control Budget Model)')),
      'Existing to Remain':  num(lk('Existing to Remain Factor (from Control Budget Model)')),     // singular 'Model'; defaults to 0 (no cost)
    },
    // Pool/spa is priced as a LUMP SUM by size template (not $/SF × area), keyed
    // by the Room Template name the Airtable Budget Room Cost formula switches on.
    // (Note the double space in the "Pool Only Cost  - Medium" field name.)
    poolCosts: {
      'Spa Only':             num(lk('Spa Only Cost (from Control Budget Model)')),
      'Pool Only - Small':    num(lk('Pool Only Cost - Small (from Control Budget Model)')),
      'Pool Only - Medium':   num(lk('Pool Only Cost  - Medium (from Control Budget Model)')),
      'Pool Only - Large':    num(lk('Pool Only Cost - Large (from Control Budget Model)')),
      'Pool and Spa - Small':  num(lk('Pool Spa Cost - Small (from Control Budget Model)')),
      'Pool and Spa - Medium': num(lk('Pool Spa Cost - Medium (from Control Budget Model)')),
      'Pool and Spa - Large':  num(lk('Pool Spa Cost - Large (from Control Budget Model)')),
    },
  }
  const roomIds = f['Rooms and Spaces'] || []
  const rooms = roomIds.length ? await getByIds(TBL_ROOMS, roomIds) : []
  const spaces = rooms.map(r => {
    const rf = r.fields || {}
    let cls = rf['Classification']; if (Array.isArray(cls)) cls = cls[0] || ''
    let name = rf['Custom Name'] || rf['Line Item Name'] || rf['Room Number and Name'] || r.id
    if (Array.isArray(name)) name = name[0] || ''
    return {
      id: r.id,
      name: String(name),
      cls: cls || 'Occupiable',
      netSF: num(rf['Room Area Net']),
      cf: num(rf['Cost Factor']) || 1,
      scope: rf['Scope Type'] || 'New Construction',
      tplId: (rf['Room Template'] || [])[0] || null,   // resolved to a name below for pools
    }
  }).filter(s => s.netSF > 0 || s.cls === 'Pool Spa')   // pools have no meaningful net SF

  // Resolve Room Template names for pool/spa spaces (the lump-sum switch key).
  const tplIds = [...new Set(spaces.filter(s => s.cls === 'Pool Spa').map(s => s.tplId).filter(Boolean))]
  if (tplIds.length) {
    const tpls = await getByIds(TBL_ROOM_TPL, tplIds)
    const nameById = {}; tpls.forEach(t => { nameById[t.id] = (t.fields || {})['Name'] || '' })
    spaces.forEach(s => { if (s.cls === 'Pool Spa') s.template = nameById[s.tplId] || '' })
  }
  spaces.forEach(s => { delete s.tplId })
  return { spaces, overrides }
}

/* ── Integrated Design Proposal ──────────────────────────────────────────── */

// The moodboard mirrors the CF deck: every CF theme slot (CF_THEMES × CF_MAX_SLOTS),
// round-robin (all Image 1s, then Image 2s, …) for column variety. Each slot carries its
// theme section id + index + default layout so MoodboardMasonry can cap each theme at its
// chosen CF layout count — the board shows exactly the images placed on the CF presentation.
function idpMoodboardSlots(f) {
  const out = []
  for (let k = 0; k < CF_MAX_SLOTS; k++) {
    CF_THEMES.forEach(t => {
      out.push({
        ...fieldSlot(f, t.field + ' Image ' + (k + 1)),
        themeSection: 'theme:' + t.key, slotIndex: k, defaultLayout: t.layout,
      })
    })
  }
  return out
}

// Rendering sections (presentation order): elevations + bird's eye are single
// full-width renderings; Entry is a portrait+landscape pair; Detail Views is a
// 4-up. Each carries its image field(s) + an optional editable title override.
const IDP_RENDERINGS = [
  { key: 'existing-elev', layout: 'rendering', titleField: 'Existing Street Elevation Title Override', def: 'Existing Street Elevation', fields: ['Existing Street Elevation Rendering'] },
  { key: 'street-elev',   layout: 'rendering', titleField: 'Street Elevation Title Override',          def: 'Street Elevation',          fields: ['Street Elevation Rendering'] },
  { key: 'rear-elev',     layout: 'rendering', titleField: 'Rear Elevation Title Override',            def: 'Rear Elevation',            fields: ['Rear Elevation Rendering'] },
  { key: 'birds-eye',     layout: 'rendering', titleField: 'Birds Eye Title Override',                 def: "Bird's Eye View",           fields: ['Birds Eye Rendering'] },
  { key: 'entry',         layout: 'entry',     titleField: 'Entry Details Title Override',             def: 'Entry Detail',              fields: ['Entry Detail Vertical Rendering', 'Entry Detail Horizontal Rendering'] },
  { key: 'detail-views',  layout: '4alt',      titleField: 'Detail Views Title Override',              def: 'Details',                   fields: ['Detail Square Rendering 1', 'Detail Square Rendering 2', 'Detail Vertical Rendering 1', 'Detail Vertical Rendering 2'] },
]

async function buildIdpSections(record) {
  const f = record.fields || {}
  const sections = []
  const city = f['Project City'] || ''
  const state = (f['Project State (from Project)'] || [])[0] || f['Project State (from Project)'] || ''

  sections.push({ id: 'splash', type: 'splash', data: { title: 'Integrated Design Proposal', subtitle: f['Project Name'] || '', location: [city, state].filter(Boolean).join(', ') } })

  // Parti (concept title + brief statement) removed per request — the deck goes straight from
  // the splash into the moodboard now. Parti Pris Title / Parti Statement stay in Airtable —
  // Parti Pris Title is also the concept's display name throughout the app's outline (see
  // ZoomTitle.jsx / DesignSection.jsx) — but neither is rendered in the IDP deck anymore.

  // Moodboard — round-robin CF theme slots (MoodboardMasonry renders the CF-placed pins).
  sections.push({
    id: 'moodboard', type: 'moodboard',
    data: { heading: 'Mood Board', images: idpMoodboardSlots(f) },
  })

  // Materials board — IDP uses the bare 'Concept Materials' + 'Material Board Layout' fields.
  const matIds = f['Concept Materials'] || []
  const materials = matIds.length ? await getByIds(TBL_MATERIAL, matIds, 'Sort Order') : []
  const matLayout = safeJson(f['Material Board Layout'], {})
  sections.push({
    id: 'materials', type: 'materials',
    data: { chips: buildMaterialChips(materials, matLayout), layoutField: 'Material Board Layout', linkField: 'Concept Materials' },
  })

  // Sketches
  const sketches = await getSketches(record.id, 'Integrated Design Proposal')
  sections.push({ id: 'sketches', type: 'sketches', data: { sketches, includeValue: 'Integrated Design Proposal', projectId: (f['Project'] || [])[0] || null } })

  // Rendering sections (each hides when empty in present mode).
  IDP_RENDERINGS.forEach((r, i) => {
    sections.push({
      id: 'rendering:' + r.key, type: 'narrative',
      data: {
        heading: (r.titleField && f[r.titleField]) || r.def,
        headingField: r.titleField || undefined,
        headingDefault: r.def,
        layout: r.layout,
        images: r.fields.map(fn => fieldSlot(f, fn)),
        bg: i % 2 === 0 ? 'bg' : 'card',
      },
    })
  })

  // Budget Explorer (control budget) — same module as CF, bottom of the deck
  sections.push({ id: 'budget', type: 'budget', data: await getBudgetData(record) })

  return sections
}

const TYPE_BUILDERS = {
  icp: { build: buildIcpSections, publishField: 'ICP Presentation', editHistoryField: 'Image Edit History', adjustField: 'ICP Image Adjustments' },
  cf:  { build: buildCfSections,  publishField: 'Conceptual Framework Presentation', editHistoryField: 'CF Image Edit History', adjustField: 'Conceptual Framework Image Adjustments' },
  idp: { build: buildIdpSections, publishField: 'IDP Presentation', editHistoryField: 'IDP Image Edit History', adjustField: 'IDP Image Adjustments' },
  idr: { build: async () => [],   publishField: null },
}

function presentationTitle(type) {
  return {
    cf: 'Conceptual Framework', idp: 'Integrated Design Proposal',
    icp: 'Interior Concept Package', idr: 'Interior Design Refinement',
  }[type] || 'Presentation'
}

/* ── Public read API ─────────────────────────────────────────────────────── */

/**
 * Load a presentation by its Project Concepts record id and type.
 * @param {string} recordId  Project Concepts record (recXXX)
 * @param {string} type      'icp' | 'cf' | 'idp' | 'idr' (default 'icp')
 * @returns normalized presentation object, or null if the record is missing.
 */
export async function getPresentation(recordId, type = 'icp') {
  if (!recordId) return null
  const record = await apiGetOne(TBL_CONCEPT, recordId)
  if (!record) return null
  const f = record.fields || {}
  const builder = TYPE_BUILDERS[type] || TYPE_BUILDERS.icp
  const sections = await builder.build(record)
  return {
    id: recordId,
    recordId,
    type,
    title: presentationTitle(type),
    project: {
      name: f['Project Name'] || '',
      city: f['Project City'] || '',
      state: (f['Project State (from Project)'] || [])[0] || f['Project State (from Project)'] || '',
    },
    publishState: builder.publishField && f[builder.publishField] ? 'published' : 'draft',
    editHistoryField: builder.editHistoryField || 'Image Edit History',
    adjustField: builder.adjustField || null,
    raw: f,
    sections,
  }
}

/**
 * Resolve the presentation for a milestone. The presentation↔milestone link is
 * by deliverable TYPE within the project: find the project's Selected Concept
 * row and pair it with the milestone's deliverable type.
 * @returns { recordId, type } or null.
 */
export async function getPresentationByMilestone(msId) {
  if (!msId) return null
  const ms = await apiGetOne(TBL_DELIV, msId)
  if (!ms) return null
  const delTypeId = (ms.fields['Deliverable'] || [])[0] || ''
  const type = PRESENTATION_TYPE_KEY[delTypeId]
  if (!type) return null

  // Scope concepts to this milestone's project. On Project Deliverables the
  // field is "Project Record UID from Project" (no parentheses); it may be a
  // lookup array or a scalar — handle both.
  const rawUid = ms.fields['Project Record UID from Project'] ||
                 ms.fields['Project Record UID (from Project)'] || ''
  const projUid = Array.isArray(rawUid) ? (rawUid[0] || '') : rawUid
  if (!projUid) return null
  const formula = 'FIND("' + projUid + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  const concepts = await apiGet(TBL_CONCEPT, 'filterByFormula=' + encodeURIComponent(formula) + '&pageSize=20')
  if (!concepts.length) return null
  const selected = concepts.find(c => c.fields['Selected Concept'] === true) || concepts[0]
  return { recordId: selected.id, type }
}

/* ── Write API (used in Phase 2 edit mode) ───────────────────────────────── */

/**
 * PATCH presentation fields on the Project Concepts record.
 * For JSON-blob fields (e.g. 'ICP Material Board Layout'), pass the already-
 * merged object; callers preserve merge-before-write semantics (the ICP tool
 * merged into a window cache before PATCH — keep that contract).
 */
export function patchPresentation(recordId, fields) {
  return apiPatch(TBL_CONCEPT, recordId, fields)
}

/** PATCH a Rooms record (e.g. Room Notes, ICP Images). */
export function patchRoom(roomId, fields) {
  return apiPatch(TBL_ROOMS, roomId, fields)
}

/** Convenience: write a material-board layout blob. The blob field differs by
    type (ICP: 'ICP Material Board Layout'; CF/IDP: 'Material Board Layout'). */
export function saveMaterialLayout(recordId, layoutObj, layoutField = 'ICP Material Board Layout') {
  return patchPresentation(recordId, { [layoutField]: JSON.stringify(layoutObj || {}) })
}

/* ── Materials records ───────────────────────────────────────────────────── */

/** Create a Materials record; returns its id. */
export async function createMaterialRecord(name, url, sortOrder) {
  const res = await apiPost(TBL_MATERIAL, {
    'Material Name': name || 'Material',
    'Material Image': [{ url }],
    'Sort Order': sortOrder || 1,
  })
  return res && res.id
}

/** Set the concept's linked materials. Link field differs by type
    (ICP: 'ICP Concept Materials'; CF/IDP: 'Concept Materials'). */
export function linkConceptMaterials(recordId, ids, linkField = 'ICP Concept Materials') {
  return patchPresentation(recordId, { [linkField]: ids || [] })
}

/** Inline-rename a material. */
export function renameMaterial(matId, name) {
  return apiPatch(TBL_MATERIAL, matId, { 'Material Name': name })
}

/* ── Room collage elements (tblcsAtLfjVAPZLB4) ───────────────────────────── */

const COLLAGE_DEFAULT_POS = { x: 20, y: 10, scale: 1, rotation: 0, z: 1 }

/** Create a collage element on a room; returns its id. */
export async function createCollageElement(roomId, url, label, category) {
  const res = await apiPost(TBL_COLLAGE, {
    'Image': [{ url }],
    'Label': label || '',
    'Category': category || 'Fixture',
    'Room': [roomId],
    'Position': JSON.stringify(COLLAGE_DEFAULT_POS),
  })
  return res && res.id
}

/** Create a collage element on a presentation SECTION (CF), linked to the concept
    + tagged with its Concept Section label. Returns its id. */
export async function createSectionCollageElement(conceptId, sectionLabel, url, label, category) {
  const res = await apiPost(TBL_COLLAGE, {
    'Image': [{ url }],
    'Label': label || '',
    'Category': category || 'Fixture',
    'Linked Concept': [conceptId],
    'Concept Section': sectionLabel,
    'Position': JSON.stringify(COLLAGE_DEFAULT_POS),
  })
  return res && res.id
}

/** Persist a collage element's Position { x, y (%), scale, rotation, z }. */
export function updateCollagePosition(elId, pos) {
  return apiPatch(TBL_COLLAGE, elId, { 'Position': JSON.stringify(pos) })
}

export function deleteCollageElement(elId) {
  return apiDelete(TBL_COLLAGE, elId)
}

/* ── Sketches (tblQYaWR9pUmiAwZe) ────────────────────────────────────────── */

/** Create a sketch under the concept; returns its id. `includeValue` is the
    Concept Sketches "Include in Package" tag (per presentation type) so the
    sketch shows in the right tool — CF/IDP sketches were vanishing because this
    was hardcoded to the ICP value. */
export async function createSketch(recordId, url, sortOrder, includeValue = 'Interior Concept Package') {
  const res = await apiPost(TBL_SKETCH, {
    'Sketch File': [{ url }],
    'Sketch Title': 'Untitled Sketch',
    'Project Concepts': [recordId],
    'Include in Package': includeValue,
    'Sort Order': sortOrder || 1,
  })
  return res && res.id
}

export function patchSketch(sketchId, fields) {
  return apiPatch(TBL_SKETCH, sketchId, fields)
}

export function deleteSketch(sketchId) {
  return apiDelete(TBL_SKETCH, sketchId)
}

/** Persist a full drag-drop reorder — `orderedIds` is the sketch list in its new order;
    writes 1-based `Sort Order` to each in one batched PATCH. */
export function reorderSketches(orderedIds) {
  const records = orderedIds.map((id, i) => ({ id, fields: { 'Sort Order': i + 1 } }))
  return apiPatchBatch(TBL_SKETCH, records)
}

/* ── Image write-back + non-destructive versioning ───────────────────────── */

/** Parse a presentation's edit-history JSON blob ({ editKey: originalUrl }). */
export function parseEditHistory(rawFields, field = 'Image Edit History') {
  return safeJson((rawFields || {})[field], {}) || {}
}

/** Stable per-slot key (matches the original's format). */
export function editKey(fieldName, index, roomId) {
  return (roomId ? roomId + ':' : '') + fieldName + ':' + index
}

/* ── Image framing (per-image focal point + zoom) ────────────────────────── */

/** Parse the adjustments JSON ({ editKey: { posX, posY, scale } }). */
export function parseAdjust(rawFields, field) {
  if (!field) return {}
  return safeJson((rawFields || {})[field], {}) || {}
}

/** Persist the whole adjustments map to the type's adjust field. */
export function saveAdjust(recordId, map, field) {
  if (!field) return Promise.resolve()
  return patchPresentation(recordId, { [field]: JSON.stringify(map || {}) })
}

/**
 * Preserve the ORIGINAL url for a slot the first time it's edited — never
 * overwritten (this is what makes Revert non-destructive). No-op if already set
 * or if there is no current image. Returns the (possibly updated) history.
 */
export function preserveOriginal(recordId, history, key, currentUrl, field = 'Image Edit History') {
  const h = history || {}
  if (!currentUrl || h[key]) return h
  const next = { ...h, [key]: currentUrl }
  patchPresentation(recordId, { [field]: JSON.stringify(next, null, 2) }).catch(() => {})
  return next
}

/* Rebuild a multi-attachment array: keep untouched slots as {id} (or {url}),
   set the changed index to {url:newUrl}; drop empty slots. Pass newUrl='' to
   clear the slot. currentSlots = [{url, attId}]. */
function rebuildAttachments(currentSlots, index, newUrl) {
  const slots = (currentSlots || []).slice()
  while (slots.length <= index) slots.push({ url: '', attId: null })
  slots[index] = { url: newUrl, attId: null }   // changed slot becomes a fresh {url}
  const out = []
  slots.forEach((s, i) => {
    if (i === index) { if (newUrl) out.push({ url: newUrl }) }
    else if (s && s.url) out.push(s.attId ? { id: s.attId } : { url: s.url })
  })
  return out
}

/** Write an image into a concept attachment field at `index`. newUrl='' clears. */
export function saveImageField(recordId, fieldName, index, newUrl, currentSlots) {
  return patchPresentation(recordId, { [fieldName]: rebuildAttachments(currentSlots, index, newUrl) })
}

/** Same, for a Rooms-table attachment field (e.g. ICP Images). */
export function saveRoomImageField(roomId, fieldName, index, newUrl, currentSlots) {
  return patchRoom(roomId, { [fieldName]: rebuildAttachments(currentSlots, index, newUrl) })
}

/* Reverse map: section id → editable text field on the concept record (per type).
   Theme/brief headings are fixed labels (not stored), so only bodies persist. */
const WRITE_MAP = {
  icp: {
    'brief':            { body: 'Interior Concept Narrative' },
    'theme:materiality':{ body: 'Materiality and Atmosphere' },
    'theme:form':       { body: 'Interior Form and Features' },
    'theme:experience': { body: 'Experience and Use' },
  },
}

/**
 * Translate a section patch ({ body, heading, ... }) into the Airtable fields to
 * PATCH on the concept record. Returns null when nothing maps (no write needed).
 */
export function fieldsForPatch(type, sectionId, patch) {
  const map = (WRITE_MAP[type] || {})[sectionId]
  if (!map || !patch) return null
  const fields = {}
  Object.keys(patch).forEach(k => { if (map[k]) fields[map[k]] = patch[k] })
  return Object.keys(fields).length ? fields : null
}

/* ── Supabase image map (presentation_images table) ────────────────────────── */

/* Team calls authenticate with the Clerk bearer. The public client presentation
   view (journey token, no Clerk) instead passes its journey token as ?t= on the
   two reads it needs (images + layouts) — validated server-side against active
   client_journeys (api/_presentationAuth.js). No shared secret in the bundle. */
async function authHeaders() {
  const clerk = typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null) : null
  return {
    'Content-Type': 'application/json',
    ...(clerk ? { Authorization: 'Bearer ' + clerk } : {}),
  }
}

function journeyQs(journeyToken) {
  return journeyToken ? '&t=' + encodeURIComponent(journeyToken) : ''
}

/**
 * Fetch all image slots for a concept as an imageMap: { [slotKey]: { url, storagePath } }.
 * Entries with url='' are tombstones (slot intentionally cleared; overrides Airtable fallback).
 * Pass `journeyToken` on the public client view (no Clerk) — authorizes the read.
 */
export async function getConceptImages(conceptId, journeyToken) {
  if (!conceptId) return {}
  try {
    const res = await fetch('/api/presentation-images?conceptId=' + encodeURIComponent(conceptId) + journeyQs(journeyToken), {
      headers: await authHeaders(),
    })
    if (!res.ok) return {}
    const { images } = await res.json()
    const map = {}
    ;(images || []).forEach(row => { map[row.slot_key] = { url: row.url || '', storagePath: row.storage_path || null } })
    return map
  } catch { return {} }
}

/** Upsert one image slot. url='' acts as a tombstone (masks any Airtable fallback). */
export async function upsertConceptImage(conceptId, slotKey, url, storagePath) {
  await fetch('/api/presentation-images', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ action: 'upsert', conceptId, slotKey, url: url || '', storagePath: storagePath || null }),
  })
}

/** Swap two image slots atomically. Pass effective URLs and storage paths for both. */
export async function swapConceptImages(conceptId, slotA, urlA, pathA, slotB, urlB, pathB) {
  await fetch('/api/presentation-images', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ action: 'swap', conceptId, slotA, urlA: urlA || '', pathA: pathA || null, slotB, urlB: urlB || '', pathB: pathB || null }),
  })
}

/* ── Media Library (holding tank — presentation_library table) ──────────────── */

/** Fetch a concept's explicit library rows: [{ id, url, storagePath, source, createdAt }]. */
export async function getConceptLibrary(conceptId) {
  if (!conceptId) return []
  try {
    const res = await fetch('/api/presentation-library?conceptId=' + encodeURIComponent(conceptId), {
      headers: await authHeaders(),
    })
    if (!res.ok) return []
    const { library } = await res.json()
    return (library || []).map(r => ({ id: r.id, url: r.url || '', storagePath: r.storage_path || null, source: r.source || 'upload', createdAt: r.created_at }))
  } catch { return [] }
}

/** Add an image to the pool (idempotent on url). Returns the row id, or null. */
export async function addLibraryImage(conceptId, url, storagePath, source = 'upload') {
  if (!conceptId || !url) return null
  try {
    const res = await fetch('/api/presentation-library', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ action: 'add', conceptId, url, storagePath: storagePath || null, source }),
    })
    if (!res.ok) return null
    const { id } = await res.json()
    return id || null
  } catch { return null }
}

/** Remove a library row by id (does NOT unplace the image from any frame). */
export async function deleteLibraryImage(conceptId, id) {
  if (!conceptId || !id) return
  await fetch('/api/presentation-library', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ action: 'delete', conceptId, id }),
  }).catch(() => {})
}

/* ── Per-section grid layout choice (presentation_layouts table) ────────────── */

/** Fetch a concept's chosen section layouts → { [sectionKey]: layoutId }.
    Pass `journeyToken` on the public client view (no Clerk) — authorizes the read. */
export async function getConceptLayouts(conceptId, journeyToken) {
  if (!conceptId) return {}
  try {
    const res = await fetch('/api/presentation-layouts?conceptId=' + encodeURIComponent(conceptId) + journeyQs(journeyToken), {
      headers: await authHeaders(),
    })
    if (!res.ok) return {}
    const { layouts } = await res.json()
    const map = {}
    ;(layouts || []).forEach(r => { map[r.section_key] = r.layout })
    return map
  } catch { return {} }
}

/** Set one section's layout choice (idempotent on section_key). */
export async function setConceptLayout(conceptId, sectionKey, layout) {
  if (!conceptId || !sectionKey || !layout) return
  await fetch('/api/presentation-layouts', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ action: 'set', conceptId, sectionKey, layout }),
  }).catch(() => {})
}

/**
 * Enumerate every image slot across a presentation's sections, computing each
 * slot's stable slotKey the SAME way ImageLayoutGrid/MoodboardMasonry do
 * (per-slot field → editKey(field,0); shared array field → editKey(imgField,i)).
 * Returns [{ slotKey, baseUrl }] — baseUrl is the Airtable attachment value
 * (before any imageMap override). Used to build the drawer pool + "used" set.
 */
export function collectSlots(presentation) {
  const out = []
  const sections = (presentation && presentation.sections) || []
  for (const s of sections) {
    const d = s.data || {}
    const images = d.images
    if (!Array.isArray(images)) continue
    images.forEach((slot, i) => {
      if (!slot) return
      const field = slot.field || d.imgField
      if (!field) return
      const idx = slot.field ? 0 : i
      out.push({ slotKey: editKey(field, idx), baseUrl: slot.url || '' })
    })
  }
  return out
}
